/**
 * MIRRORED FILE — src/utils/quotationLineAmount.js <-> client/src/utils/quotationLineAmount.ts
 * Change one, change both. `src/__tests__/mirroredUtilParity.test.js` is the
 * list and the enforcement.
 *
 * ---------------------------------------------------------------------------
 * DOES A QUOTATION LINE STILL STATE THE AMOUNT THE VESSEL ASKED FOR? (D51)
 * ---------------------------------------------------------------------------
 *
 * The server twin carries the full history (QUO-00054: 20 CASE of water quoted
 * as 20 L). In short: a line quoted in a unit other than the one the vessel
 * asked in must state the ask restated into its unit through the line's own
 * pack, or it is `mismatch` / `unverified` and the quotation may not be
 * Finalized, Sent or confirmed until the amount is corrected, rounded up on
 * purpose (`partPackRounding`), or said to be right (`amountAccepted`).
 *
 * The grid reads this to flag the Quantity cell and list the lines before the
 * click; the server is the gate (`quotationStatus.assertStatusAllowed`,
 * `quotationConfirmationRules.confirmationBlockers`).
 */

import { isSameUnit } from './unitVocabulary';
import { convertQuantity, roundQuantity } from './packConversion';
// The whole-number conversion rule's one owner (D64, F5 2026-09-25 audit) —
// whether `expected` below is a POSITIVE fractional count of a unit that
// counts things. Read only by `expectedIsWholeCount`; it changes nothing about
// `status` or `blocksQuotation` (see that function's own header).
import { isFractionalWholeCount } from './unitConversionGraph';

export const LINE_AMOUNT_STATUS = Object.freeze({
  OK: 'ok',
  MISMATCH: 'mismatch',
  UNVERIFIED: 'unverified',
  ACCEPTED: 'accepted',
} as const);

export type LineAmountStatus = (typeof LINE_AMOUNT_STATUS)[keyof typeof LINE_AMOUNT_STATUS];

/** The machine-readable reason a line blocks Finalize / Send / Confirm. */
export const LINE_AMOUNT_BLOCKER_CODE = 'QUOTATION_LINE_AMOUNT_NOT_THE_ASK';

export type LineAmountCheck = {
  status: LineAmountStatus;
  asked: { quantity: number; uom: string } | null;
  quoted: { quantity: number | null; uom: string | null };
  /** The ask restated in the line's unit, when the pack can say. */
  expected: number | null;
};

export type LineAmountBlocker = LineAmountCheck & {
  code: string;
  lineNumber: number;
  itemName: string;
  message: string;
};

type LineLike = {
  quantity?: unknown;
  uom?: unknown;
  lineNumber?: number | null;
  itemName?: string | null;
  originalRequest?: { quantity?: unknown; uom?: unknown } | null;
  pricingDetails?: unknown;
};

function unitOf(value: unknown): string | null {
  const u = String(value == null ? '' : value).trim();
  return u || null;
}

/** Two quantities that agree to the precision every restatement is written at. */
function sameAmount(a: unknown, b: unknown): boolean {
  return roundQuantity(a) != null && roundQuantity(a) === roundQuantity(b);
}

function detailsOf(line: LineLike | null | undefined): Record<string, any> {
  const pd = line && line.pricingDetails;
  return pd && typeof pd === 'object' ? (pd as Record<string, any>) : {};
}

/** The line's own pack — the one the grid's Unit dropdown restates through. */
function packOf(line: LineLike): unknown {
  const de = detailsOf(line).displayEquivalents;
  return de && typeof de === 'object' ? de.packStructure || null : null;
}

/** A round-up a person chose, to exactly the figure the ask restates to. */
function isRecordedRoundUp(line: LineLike, quoted: LineAmountCheck['quoted'], expected: number | null): boolean {
  const r = detailsOf(line).partPackRounding;
  if (!r || typeof r !== 'object' || expected == null) return false;
  return isSameUnit(r.uom, quoted.uom) && sameAmount(r.to, quoted.quantity) && sameAmount(r.from, expected);
}

/** "The amount is right", said about exactly this quantity and unit. */
function isAccepted(line: LineLike, quoted: LineAmountCheck['quoted']): boolean {
  const a = detailsOf(line).amountAccepted;
  if (!a || typeof a !== 'object') return false;
  return isSameUnit(a.uom, quoted.uom) && sameAmount(a.quantity, quoted.quantity);
}

/** Judge one line. */
export function lineAmountCheck(line: LineLike | null | undefined): LineAmountCheck {
  const req = (line && line.originalRequest) || {};
  const askedQty = roundQuantity(req.quantity);
  const askedUom = unitOf(req.uom);
  const asked = askedQty != null && askedUom ? { quantity: askedQty, uom: askedUom } : null;
  const quoted = { quantity: roundQuantity(line && line.quantity), uom: unitOf(line && line.uom) };

  if (!line || !asked || quoted.quantity == null || !quoted.uom) {
    return { status: LINE_AMOUNT_STATUS.OK, asked, quoted, expected: null };
  }
  if (isSameUnit(asked.uom, quoted.uom)) {
    return { status: LINE_AMOUNT_STATUS.OK, asked, quoted, expected: asked.quantity };
  }

  const expected = roundQuantity(
    convertQuantity({ quantity: asked.quantity, fromUom: asked.uom, toUom: quoted.uom, pack: packOf(line) })
  );
  let status: LineAmountStatus;
  if (expected == null) status = LINE_AMOUNT_STATUS.UNVERIFIED;
  else if (sameAmount(quoted.quantity, expected) || isRecordedRoundUp(line, quoted, expected)) {
    status = LINE_AMOUNT_STATUS.OK;
  } else status = LINE_AMOUNT_STATUS.MISMATCH;

  if (status !== LINE_AMOUNT_STATUS.OK && isAccepted(line, quoted)) status = LINE_AMOUNT_STATUS.ACCEPTED;
  return { status, asked, quoted, expected };
}

/** Does this verdict stop the quotation being Finalized, Sent or confirmed? */
export function blocksQuotation(check: LineAmountCheck | null | undefined): boolean {
  return !!check && (check.status === LINE_AMOUNT_STATUS.MISMATCH || check.status === LINE_AMOUNT_STATUS.UNVERIFIED);
}

/**
 * May the D51 "use the asked amount" chip write `check.expected` straight into
 * the quantity cell? Owner decision A, F5 (2026-09-25 audit): D51 never offers
 * a fractional count of a whole-count unit — the ask restated into the line's
 * own unit can be honest arithmetic ("3.333333 CASE") and still be nothing a
 * vendor ships, exactly the reasoning `isFractionalWholeCount` already applies
 * to a hand-typed quantity.
 *
 * `false` here changes NOTHING about `status` or `blocksQuotation` — a
 * MISMATCH stays a MISMATCH, and Finalize/Send/Confirm stay refused by it
 * either way. It answers a narrower, UI-only question: is `expected` itself
 * something to offer as a one-click fix, or must the caller offer the other
 * two D51 paths instead (switch to the asked unit and reprice there, or type a
 * whole number by hand)? "Keep this amount" (`amountAccepted`) is untouched by
 * this in every case — it says the line is right as it stands, never that
 * `expected` was written.
 */
export function expectedIsWholeCount(check: LineAmountCheck | null | undefined): boolean {
  if (!check || check.expected == null || !check.quoted.uom) return true;
  return !isFractionalWholeCount(check.expected, check.quoted.uom);
}

function formatQuantity(q: unknown): string {
  const r = roundQuantity(q);
  return r == null ? '' : String(r);
}

/** One sentence a person can act on. The `code` beside it is the contract. */
export function describeLineAmount(check: LineAmountCheck | null | undefined): string {
  if (!check || !check.asked) return '';
  const asked = `${formatQuantity(check.asked.quantity)} ${check.asked.uom}`;
  const quoted = `${formatQuantity(check.quoted.quantity)} ${check.quoted.uom}`;
  if (check.status === LINE_AMOUNT_STATUS.MISMATCH) {
    return `The vessel asked for ${asked}, which is ${formatQuantity(check.expected)} ${check.quoted.uom}; the line says ${quoted}.`;
  }
  if (check.status === LINE_AMOUNT_STATUS.UNVERIFIED) {
    return `The vessel asked for ${asked}; the line says ${quoted}, and nothing on it says how many ${check.quoted.uom} that is.`;
  }
  return '';
}

/** Every line whose amount blocks the quotation, in line order. */
export function lineAmountBlockers(items: unknown): LineAmountBlocker[] {
  const out: LineAmountBlocker[] = [];
  (Array.isArray(items) ? (items as LineLike[]) : []).forEach((item, index) => {
    const check = lineAmountCheck(item);
    if (!blocksQuotation(check)) return;
    const lineNumber = item && item.lineNumber != null ? item.lineNumber : index + 1;
    const itemName = (item && item.itemName) || '';
    out.push({
      code: LINE_AMOUNT_BLOCKER_CODE,
      lineNumber,
      itemName,
      status: check.status,
      asked: check.asked,
      quoted: check.quoted,
      expected: check.expected,
      message: `Line ${lineNumber}${itemName ? ` (${itemName})` : ''}: ${describeLineAmount(check)}`,
    });
  });
  return out;
}
